import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { CustomerDisplaySnapshot } from '@/lib/schemas/customer-display-state-schema'
import { useAuth } from '@/hooks/use-auth'
import { useCustomerDisplaySSE } from '@/hooks/use-customer-display-sse'
import { ApiError } from '@/lib/api/client'
import { salesOrderApi } from '@/lib/api/sales-order-api'
import { createCustomerDisplayStateSchema } from '@/lib/schemas/customer-display-state-schema'

export const customerDisplayKeys = {
    state: (userID: string | undefined) =>
        ['customer-display', userID] as const,
}
type ReadResult = {
    snapshot?: CustomerDisplaySnapshot
    supported: boolean
    deadline: number | null
}
type LegacyEvent =
    | Pick<Extract<CustomerDisplaySnapshot, { type: 'cart' }>, 'type' | 'data'>
    | Pick<
          Extract<CustomerDisplaySnapshot, { type: 'checkout' }>,
          'type' | 'data'
      >
    | Pick<Extract<CustomerDisplaySnapshot, { type: 'qr' }>, 'type' | 'data'>
    | Pick<Extract<CustomerDisplaySnapshot, { type: 'paid' }>, 'type' | 'data'>
    | { type: 'clear'; data: null }

/** SSE hints trigger authoritative reads; the shared stream owns transport recovery. */
export function useCustomerDisplayQuery() {
    const { user } = useAuth()
    const userID = user?.id
    const qc = useQueryClient()
    const queryKey = useMemo(() => customerDisplayKeys.state(userID), [userID])
    const [legacy, setLegacy] = useState<{
        userID: string | undefined
        value: ReadResult
    }>()
    const recovery = useMemo<{
        legacy?: { barrier?: number }
        generation: number
        readGeneration: number
        pending?: Promise<void>
    }>(() => ({ generation: 0, readGeneration: 0 }), [userID])
    const [invalid, setInvalid] = useState(false)
    const [clock, setClock] = useState(Date.now)
    const query = useQuery({
        queryKey,
        enabled: Boolean(userID),
        retry: false,
        refetchInterval: 5_000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: 'always',
        queryFn: async ({ signal }): Promise<ReadResult> => {
            const previous = qc.getQueryData<ReadResult>(queryKey)
            const generation = recovery.generation
            recovery.readGeneration = generation
            try {
                const response = await salesOrderApi.displayState(signal)
                signal.throwIfAborted()
                const snapshot = createCustomerDisplayStateSchema().parse(
                    response.data,
                )
                if (
                    previous?.snapshot &&
                    snapshot.revision < previous.snapshot.revision
                ) {
                    throw new Error('Customer display revision moved backwards')
                }
                setInvalid(false)
                const fallback = recovery.legacy
                // The first read after a legacy gap may predate even the latest SSE event.
                if (
                    fallback &&
                    (fallback.barrier === undefined ||
                        generation !== recovery.generation ||
                        snapshot.revision <= fallback.barrier)
                ) {
                    fallback.barrier = Math.max(
                        fallback.barrier ?? 0,
                        snapshot.revision,
                    )
                } else {
                    recovery.legacy = undefined
                    setLegacy(undefined)
                }
                return {
                    snapshot,
                    supported: true,
                    deadline:
                        snapshot.expires_at === null
                            ? null
                            : Date.now() +
                              Date.parse(snapshot.expires_at) -
                              Date.parse(snapshot.server_time),
                }
            } catch (error) {
                signal.throwIfAborted()
                if (error instanceof ApiError && error.status === 404) {
                    if (recovery.legacy) recovery.legacy.barrier = undefined
                    return {
                        ...previous,
                        supported: false,
                        deadline: previous?.deadline ?? null,
                    }
                }
                throw error
            }
        },
    })
    const retry = useCallback(() => {
        if (!recovery.pending) {
            recovery.pending = (async () => {
                const refetch = () =>
                    qc.refetchQueries(
                        { queryKey, exact: true, type: 'active' },
                        { cancelRefetch: false },
                    )
                const read = refetch()
                const generation = recovery.readGeneration
                await read
                // Collapse hints received during this read into one follow-up.
                if (
                    generation !== recovery.generation &&
                    qc.getQueryState(queryKey)?.fetchStatus === 'idle'
                )
                    await refetch()
            })().finally(() => {
                recovery.pending = undefined
            })
        }
        return recovery.pending
    }, [qc, queryKey, recovery])
    const accept = useCallback(
        (event: LegacyEvent) => {
            const current = qc.getQueryData<ReadResult>(queryKey)
            recovery.generation++
            if (current?.supported && !recovery.legacy) {
                void retry()
                return
            }
            const ttl =
                event.type === 'checkout'
                    ? 3_000
                    : event.type === 'paid'
                      ? 5_000
                      : null
            const now = Date.now()
            recovery.legacy = {
                barrier: current?.supported
                    ? recovery.legacy?.barrier
                    : undefined,
            }
            setLegacy({
                userID,
                value: {
                    supported: false,
                    deadline: ttl === null ? null : now + ttl,
                    snapshot: {
                        ...event,
                        revision: 0,
                        updated_at: null,
                        expires_at:
                            ttl === null
                                ? null
                                : new Date(now + ttl).toISOString(),
                        server_time: new Date(now).toISOString(),
                    },
                },
            })
            setInvalid(false)
            if (current?.supported || qc.isFetching({ queryKey, exact: true }))
                void retry()
        },
        [qc, queryKey, recovery, retry, userID],
    )
    const { connected } = useCustomerDisplaySSE({
        onCart: (data) => accept({ type: 'cart', data }),
        onCheckout: (data) => accept({ type: 'checkout', data }),
        onShowQR: (data) => accept({ type: 'qr', data }),
        onPaid: (data) => accept({ type: 'paid', data }),
        onClear: () => accept({ type: 'clear', data: null }),
        onValidationError: () => {
            setInvalid(true)
            void retry()
        },
    })
    useEffect(() => {
        if (connected) void retry()
    }, [connected, retry])
    useEffect(() => {
        const resync = () => {
            if (document.visibilityState === 'visible') void retry()
        }
        document.addEventListener('visibilitychange', resync)
        window.addEventListener('focus', resync)
        return () => {
            document.removeEventListener('visibilitychange', resync)
            window.removeEventListener('focus', resync)
        }
    }, [retry])
    const fallback = legacy?.userID === userID ? legacy?.value : undefined
    const read = fallback ?? query.data
    useEffect(() => {
        setClock(Date.now())
        if (read?.deadline == null) return
        const timeout = setTimeout(
            () => setClock(Date.now()),
            Math.max(0, read.deadline - Date.now()),
        )
        return () => clearTimeout(timeout)
    }, [read])
    const snapshot = read?.snapshot
    const expired = read?.deadline != null && read.deadline <= clock
    const effective: CustomerDisplaySnapshot | undefined =
        expired && snapshot
            ? { ...snapshot, type: 'clear', data: null }
            : snapshot
    return {
        snapshot: effective,
        error: query.error,
        connected,
        status:
            query.isError || invalid
                ? 'stale'
                : fallback
                  ? 'legacy'
                  : query.data?.supported
                    ? 'fresh'
                    : query.data
                      ? 'legacy'
                      : 'loading',
        retry,
    }
}
